import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { toast } from "sonner";
import { Bell, Check, Trash2, Loader2, CalendarClock, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toLocalInput, fromLocalInput, presetISO, formatDue } from "../lib/reminders";

const inputClass =
  "w-full bg-[#0B0F17] border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors";

export default function ReminderDialog({ open, onClose, card, onSaved }) {
  const [at, setAt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (card) {
      setAt(toLocalInput(card.reminder_at));
      setNote(card.reminder_note || "");
    }
  }, [card]);

  if (!card) return null;
  const hasExisting = card.reminder_at && !card.reminder_done;

  const patch = async (payload, msg) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/cards/${card.id}/reminder`, payload);
      onSaved(data);
      toast.success(msg);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const iso = fromLocalInput(at);
    if (!iso) {
      toast.error("Pick a date and time");
      return;
    }
    patch({ reminder_at: iso, reminder_note: note, reminder_done: false }, "Follow-up reminder set");
  };

  const setPreset = (days) => setAt(toLocalInput(presetISO(days)));

  const presetBtn =
    "flex-1 py-2 rounded-lg border border-slate-700 text-slate-300 hover:border-blue-500 hover:text-blue-400 text-xs font-medium transition-colors";

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-md" testId="reminder-dialog">
      <div className="p-6">
        <p className="overline text-blue-500 mb-1">Follow-up nudge</p>
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">
          Remind me about {card.name || card.company}
        </h2>
        <p className="text-sm text-slate-400 mb-5">
          Never let a promising lead go cold — pick when you want a nudge.
        </p>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setPreset(1)} className={presetBtn}>Tomorrow</button>
          <button onClick={() => setPreset(3)} className={presetBtn}>In 3 days</button>
          <button onClick={() => setPreset(7)} className={presetBtn}>Next week</button>
        </div>

        <label className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
          <CalendarClock className="w-3.5 h-3.5" /> When
        </label>
        <input
          data-testid="reminder-datetime-input"
          type="datetime-local"
          value={at}
          onChange={(e) => setAt(e.target.value)}
          className={`${inputClass} mb-4 [color-scheme:dark]`}
        />

        <label className="text-xs text-slate-400 mb-1 block">Note (optional)</label>
        <input
          data-testid="reminder-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Send the Q3 proposal"
          className={`${inputClass} mb-5`}
        />

        <button
          data-testid="reminder-save-button"
          onClick={save}
          disabled={busy}
          className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60 mb-3"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4" /> {hasExisting ? "Update reminder" : "Set reminder"}</>}
        </button>

        <p className="text-xs text-slate-500 mt-3 mb-1 text-center flex items-center justify-center gap-1.5">
          <Mail className="w-3.5 h-3.5" /> We'll email you when it's due — even if the app is closed
        </p>

        {hasExisting && (
          <div className="flex gap-3">
            <button
              data-testid="reminder-done-button"
              onClick={() => patch({ reminder_done: true }, "Marked as followed up")}
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl border border-emerald-600/40 text-emerald-400 hover:bg-emerald-500/10 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <Check className="w-4 h-4" /> Mark done
            </button>
            <button
              data-testid="reminder-clear-button"
              onClick={() => patch({ reminder_at: "", reminder_note: "", reminder_done: false }, "Reminder cleared")}
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 hover:text-red-400 hover:border-red-500/50 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <Trash2 className="w-4 h-4" /> Clear
            </button>
          </div>
        )}

        {hasExisting && (
          <p className="text-xs text-slate-500 mt-4 text-center">
            Current: {formatDue(card.reminder_at)}
          </p>
        )}
      </div>
    </Modal>
  );
}
